import './style.scss';

import {
  faClone,
  faEdit,
  faEllipsisV,
  faPlus,
  faTrash,
  faX,
} from '@fortawesome/pro-light-svg-icons';
import { faChevronLeft, faChevronRight } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import { useContext, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import {
  DndContext,
  closestCenter,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { CurrencyTypeEnum } from '../../model/currency_type.enum';
import { DashboardTabsKeysEnum } from '../../model/dashboard-tabs';
import { DashboardContext } from '../../providers/dashboard-provider';
import { GlobalContext } from '../../providers/global-provider';
import { CustomWidget } from '../../providers/model/custom-widget';
import { clearTabPersistentState } from '../../providers/use-persistent-state';
import { WebSocketContext } from '../../providers/websocket-context/websocket-provider';
import { deleteStorage, getStorage } from '../../utils/storage_service';

import HandleCustomTabModal, { TypeOfView } from './handle-custom-tab-modal';

const Tabs = () => {
  const { marketDataOverviewCurrentTab, marketDataOverviewCurrentToken } =
    useContext(GlobalContext);
  const { tabs, selectTab, closeTab, addTab, deleteTab, renameTab, duplicateTab, setTabsOrder } =
    useContext(DashboardContext);
  const { unsubscribeAllChannelsInTabOverview, unsubscribeByWidget } = useContext(WebSocketContext);
  const [openHandleCustomTabModal, setOpenHandleCustomTabModal] = useState(false);
  const [modalType, setModalType] = useState(TypeOfView.NEW_TAB);

  const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(null);
  const menuPaperRef = useRef<HTMLDivElement | null>(null);
  const open = Boolean(menuPos);

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [hasOverflow, setHasOverflow] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = () => {
    const el = scrollRef.current;
    if (!el) return;
    const overflow = el.scrollWidth > el.clientWidth + 2;
    setHasOverflow(overflow);
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  };

  const onScroll = () => updateScrollState();

  useEffect(() => {
    updateScrollState();
    const handleResize = () => updateScrollState();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [tabs.length]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const selected = el.querySelector(
      `[data-tab-key="${marketDataOverviewCurrentTab}"]`,
    ) as HTMLElement | null;
    if (selected) {
      selected.scrollIntoView({ behavior: 'smooth', inline: 'nearest', block: 'nearest' });
    }
  }, [marketDataOverviewCurrentTab]);

  useEffect(() => {
    if (!open) return;
    const handleGlobalMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (menuPaperRef.current && menuPaperRef.current.contains(target)) return;
      e.stopPropagation();
      setMenuPos(null);
    };
    document.addEventListener('mousedown', handleGlobalMouseDown, true);
    return () => document.removeEventListener('mousedown', handleGlobalMouseDown, true);
  }, [open]);

  const scrollByPage = (direction: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    const delta = Math.max(100, Math.floor(el.clientWidth * 0.8));
    const nextLeft = direction === 'left' ? el.scrollLeft - delta : el.scrollLeft + delta;
    el.scrollTo({ left: nextLeft, behavior: 'smooth' });
    setTimeout(updateScrollState, 250);
  };

  const handleCloseTab = (e: React.MouseEvent, tabKey: string) => {
    if (marketDataOverviewCurrentToken === '' && !tabKey.startsWith('custom_tab')) return;

    if (tabKey.startsWith('overview_')) {
      const currency = tabKey.replace('overview_', '').toUpperCase();

      clearTabPersistentState(currency as CurrencyTypeEnum);
    }

    e.stopPropagation();
    closeTab(tabKey);
    unsubscribeAllChannelsInTabOverview(marketDataOverviewCurrentToken);
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
  );

  const dragOriginRef = useRef<{ id: string; rect: DOMRect } | null>(null);

  const handleDragStart = useCallback((event: any) => {
    const { active } = event;
    if (!active) return;
    if (scrollRef.current) {
      const el = scrollRef.current.querySelector(
        `[data-tab-key="${active.id}"]`,
      ) as HTMLElement | null;
      if (el) {
        dragOriginRef.current = { id: String(active.id), rect: el.getBoundingClientRect() };
      }
    }
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const oldIndex = tabs.findIndex((t) => t.key === active.id);
      const newIndex = tabs.findIndex((t) => t.key === over.id);
      if (oldIndex === -1 || newIndex === -1) return;
      const reordered = arrayMove(tabs, oldIndex, newIndex);
      if (setTabsOrder) {
        setTabsOrder(reordered.map((t) => t.key));
      }
      dragOriginRef.current = null;
    },
    [tabs, setTabsOrder],
  );

  const SortableTab = ({ tab, children }: { tab: any; children: React.ReactNode }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
      id: tab.key,
    });
    let finalTransform = transform;
    if (finalTransform) {
      finalTransform = { ...finalTransform, y: 0 };
      const container = scrollRef.current;
      if (container && dragOriginRef.current && dragOriginRef.current.id === tab.key) {
        const containerRect = container.getBoundingClientRect();
        const { rect } = dragOriginRef.current;
        const minX = containerRect.left - rect.left;
        const maxX = containerRect.right - rect.right;
        if (finalTransform.x < minX) finalTransform = { ...finalTransform, x: minX };
        if (finalTransform.x > maxX) finalTransform = { ...finalTransform, x: maxX };
      }
    }
    const style: React.CSSProperties = {
      transform: CSS.Transform.toString(finalTransform),
      transition,
      opacity: isDragging ? 0.5 : undefined,
    };
    return (
      <div
        ref={setNodeRef}
        style={style}
        {...attributes}
        {...listeners}
        className={children ? '' : ''}
        data-sortable-id={tab.key}
      >
        {children}
      </div>
    );
  };

  const renderTab = useMemo(() => {
    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
      const tabEl = (event.currentTarget as HTMLElement).closest('.tab');
      if (tabEl) {
        const rect = tabEl.getBoundingClientRect();
        setMenuPos({ top: rect.bottom, left: rect.right });
      }
    };
    const handleClose = () => {
      setMenuPos(null);
    };

    const renderMenu = () => {
      const handleClickOnOption = (event: React.MouseEvent<HTMLElement>, type: TypeOfView) => {
        event.stopPropagation();
        setModalType(type);
        setOpenHandleCustomTabModal(true);
        handleClose();
      };

      return (
        <Menu
          anchorReference="anchorPosition"
          anchorPosition={menuPos ? { top: menuPos.top, left: menuPos.left } : undefined}
          open={open}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{ ref: menuPaperRef }}
          disableAutoFocus
          disableEnforceFocus
          disableRestoreFocus
        >
          <MenuItem onClick={(e) => handleClickOnOption(e, TypeOfView.RENAME_TAB)}>
            <ListItemIcon>
              <FontAwesomeIcon icon={faEdit} />
            </ListItemIcon>
            <ListItemText>Rename tab</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={(e) => handleClickOnOption(e, TypeOfView.DUPLICATE_TAB)}
            className="menu-item"
          >
            <ListItemIcon>
              <FontAwesomeIcon icon={faClone} />
            </ListItemIcon>
            <ListItemText>Duplicate tab</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={(e) => handleClickOnOption(e, TypeOfView.DELETE_TAB)}
            className="menu-item"
          >
            <ListItemIcon>
              <FontAwesomeIcon icon={faTrash} color="#D82C0D" />
            </ListItemIcon>
            <ListItemText sx={{ color: '#D82C0D' }}>Delete tab</ListItemText>
          </MenuItem>
        </Menu>
      );
    };

    return tabs.map((tab: any) => {
      const isSelected = marketDataOverviewCurrentTab === tab.key;
      const isCustomTab = tab.key.startsWith('custom_tab');
      const isShowCloseButton =
        isSelected &&
        !isCustomTab &&
        marketDataOverviewCurrentTab !== DashboardTabsKeysEnum.volLab &&
        marketDataOverviewCurrentTab !== DashboardTabsKeysEnum.altSurfaceBuilder &&
        marketDataOverviewCurrentTab !== DashboardTabsKeysEnum.overviewBTC;

      const content = (
        <div
          key={tab.key}
          className={`tab ${isSelected ? 'tab-selected' : ''}`}
          data-tab-key={tab.key}
          onClick={() => selectTab(tab.key)}
        >
          <div className="tab-content drag-handle">
            <span>{tab.label}</span>
            {isSelected && isCustomTab && (
              <div onClick={handleClick} className="tab-button">
                <FontAwesomeIcon icon={faEllipsisV} />
              </div>
            )}
            {isShowCloseButton && (
              <Button className="tab-button" onClick={(e) => handleCloseTab(e, tab.key)}>
                <FontAwesomeIcon icon={faX} />
              </Button>
            )}
            {renderMenu()}
          </div>
        </div>
      );
      return (
        <SortableTab key={tab.key} tab={tab}>
          {content}
        </SortableTab>
      );
    });
  }, [
    tabs,
    marketDataOverviewCurrentTab,
    selectTab,
    menuPos,
    setModalType,
    setOpenHandleCustomTabModal,
  ]);

  const handleAddTab = () => {
    addTab();
  };

  const handleRenameTab = (newTab: string) => {
    renameTab(marketDataOverviewCurrentTab, newTab);
  };

  const handleDuplicateTab = (newTab: string) => {
    duplicateTab(marketDataOverviewCurrentTab, newTab);
  };

  const handleDeleteTab = () => {
    deleteTab(marketDataOverviewCurrentTab);

    const widgetsStorage = getStorage(`WIDGETS_${marketDataOverviewCurrentTab}`);

    if (widgetsStorage && widgetsStorage.widgets) {
      widgetsStorage.widgets.forEach((widget: CustomWidget) => {
        unsubscribeByWidget(widget.key);
      });
    }

    deleteStorage(`WIDGETS_${marketDataOverviewCurrentTab}`);
  };

  const handleCloseModal = () => {
    setOpenHandleCustomTabModal(false);
  };

  const onAddButton = () => {
    handleAddTab();
  };

  const verifyIfTabExists = (tabName: string) => {
    return tabs.some((tab) => tab.label.toLowerCase() === tabName.toLowerCase());
  };

  return (
    <div className="tabs-container">
      {hasOverflow && (
        <button
          className={`tabs-nav left ${canScrollLeft ? '' : 'disabled'}`}
          onClick={() => scrollByPage('left')}
          disabled={!canScrollLeft}
          aria-label="Scroll left"
        >
          <FontAwesomeIcon icon={faChevronLeft} />
        </button>
      )}

      <DndContext
        collisionDetection={closestCenter}
        sensors={sensors}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={tabs.map((t) => t.key)} strategy={horizontalListSortingStrategy}>
          <div className="tabs-scroll-container" ref={scrollRef} onScroll={onScroll}>
            {renderTab}
          </div>
        </SortableContext>
      </DndContext>

      {hasOverflow && (
        <button
          className={`tabs-nav right ${canScrollRight ? '' : 'disabled'}`}
          onClick={() => scrollByPage('right')}
          disabled={!canScrollRight}
          aria-label="Scroll right"
        >
          <FontAwesomeIcon icon={faChevronRight} />
        </button>
      )}

      <div className="tabs-add">
        <Button className="btn-new-tab" onClick={onAddButton}>
          <FontAwesomeIcon icon={faPlus} />
        </Button>
      </div>

      <HandleCustomTabModal
        handleClose={handleCloseModal}
        open={openHandleCustomTabModal}
        saveTab={handleAddTab}
        renameTab={handleRenameTab}
        deleteTab={handleDeleteTab}
        duplicateTab={handleDuplicateTab}
        typeOfView={modalType}
        verifyIfTabExists={verifyIfTabExists}
      />
    </div>
  );
};

export default Tabs;
